import { setFocusedNodeId } from 'flow/store/redux/slice';
import type { AppDispatch } from '~/store';
import type { UiEffectContextForKey, UiEffectRegistry, UiEffectRegistryKey } from './types';
import { historyAction as historyActions } from 'flow/service/history/constants';
import type { HistoryAction } from 'flow/service/history/constants';
import type { GroupUiEffectContext, ThresholdUiEffectContext, WorkflowHistoryEntry } from 'flow/store/redux/types';
import {
  DRAWER_DEBOUNCE_MS,
  historyActionTextMap,
  operationTextMap,
  undoUiEffectMap,
} from './constants';
import type { Threshold } from 'flow/store/engine/types';

export function getRuleHtmlElementId(
  rulePublicId: Threshold['publicId'],
) {
  // todo: add threshold-side
  return `rule-content-${rulePublicId}`;
}

// todo, flesh out more descriptive messages
export function getSnackbarMessage(
  historyAction: HistoryAction,
  historyEntry: WorkflowHistoryEntry,
) {
  const historyActionText = historyActionTextMap[historyAction];
  const operationText = operationTextMap[historyEntry.operation];

  return `${historyActionText} ${operationText} ${historyEntry.uiEffectContext.target}`;
}

function focusElement(elementId: string) {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
    element.focus();
  }
}

function focusDrawerElement(elementId: string) {
  setTimeout(() => focusElement(elementId), DRAWER_DEBOUNCE_MS);
}

export function buildUiEffectRegistry(dispatch: AppDispatch): UiEffectRegistry {
  return {
    'add:action': () => {
      // tbd: flesh this out
      dispatch(setFocusedNodeId(''));
    },
    'modify:threshold': (ctx: ThresholdUiEffectContext) => {
      dispatch(setFocusedNodeId(ctx.actionId));
      // todo: highlight the rhs/op/lhs, not the whole threshold
      focusDrawerElement(getRuleHtmlElementId(ctx.publicId));
    },
    'add:threshold': (ctx: ThresholdUiEffectContext) => {
      dispatch(setFocusedNodeId(ctx.actionId));
      focusDrawerElement(getRuleHtmlElementId(ctx.publicId));
    },
    'remove:threshold': (ctx: ThresholdUiEffectContext) => {
      dispatch(setFocusedNodeId(ctx.actionId));
    },
    'add:group': (ctx: GroupUiEffectContext) => {
      dispatch(setFocusedNodeId(ctx.actionId));
    },
    'modify:group': (ctx: GroupUiEffectContext) => {
      dispatch(setFocusedNodeId(ctx.actionId));
    },
    'remove:group': (ctx: GroupUiEffectContext) => {
      dispatch(setFocusedNodeId(ctx.actionId));
    },
  };
}

export function getUiEffectKey(
  historyEntry: WorkflowHistoryEntry,
  historyAction: HistoryAction,
): UiEffectRegistryKey {
  if (historyAction === historyActions.undo) {
    // When we undo a "remove" operation - it is akin to an "add" operation
    // and vice versa for "add" and "remove" operations
    const operation = undoUiEffectMap[historyEntry.operation];
    return `${operation}:${historyEntry.uiEffectContext.target}`;
  }

  return `${historyEntry.operation}:${historyEntry.uiEffectContext.target}`;
}

/**
 * Runs the handler registered for `key` with `ctx`. The generic `K` ties the
 * looked-up handler to the context type, so the invocation type-checks without
 * a cast (a correlated-union lookup).
 */
export function runUiEffect<K extends UiEffectRegistryKey>(
  uiEffectRegistry: UiEffectRegistry,
  key: K,
  ctx: UiEffectContextForKey<K>,
): void {
  uiEffectRegistry[key]?.(ctx);
}
